// src/ui/screens/standard-tests.ts
// THE FIVE DCS STANDARD TESTS — the opposite of free practice, on a phone.
//
// Backend sources, transcribed not guessed (dcs-sports-backend/sb on `main`):
//   src/practice/routes/standard-tests.ts   the six read-only routes this file calls
//   src/practice/standard-tests.ts          the protocol table, the lock, comparability, percentiles
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THIS SCREEN IS MOSTLY MADE OF REFUSALS, AND WHY THAT IS THE PRODUCT WORKING
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A Standard Test LOCKS nine session parameters plus the block size, the camera count, an fps
// floor, a device floor and the rest timing. The locking is the entire value: a March result and a
// November result are the same measurement only if every one of those was the same. So a run that
// did not meet them is not a weak test result — it is NOT A TEST, and the backend answers 200
// carrying a refusal that has no `outputs` and no `evidence_tier` for a careless `??` to reach.
//
// This screen inherits that discipline instead of restating it:
//
//   · Nothing here invents a score, a percentile, a rank or a comparison. Every number on screen
//     came out of a response body. There is no demo mode and no sample data — today the population
//     is empty, so the honest percentile surface is a refusal carrying the real counts.
//   · A refusal renders as a refusal. HTTP-level refusals go through the shipped `refusalView`;
//     the two DOMAIN refusals that arrive inside a 200 (a non-conforming run, a gated percentile)
//     get their own views, and neither of those views can render a number, because neither refusal
//     shape carries one.
//   · `sharedAxisChart()` is the ONLY function in this file that puts two runs on one axis, and it
//     returns `null` unless the server said `verdict.comparable === true`. Not a guard around the
//     drawing code — the drawing code is inside the guard, so there is no second path to it.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// AT LIMITED CALIBRATION THERE IS NO STANDARD TEST — AND THE UI MUST NOT LOOK BROKEN SAYING SO
// ══════════════════════════════════════════════════════════════════════════════════════════════
// All five protocols lock `metric_eligibility: FULL` EXACTLY, not as a floor, because a non-FULL
// capture is capped to VISION_ESTIMATE by the metric registry and stripped of its outward surfaces.
// So a LIMITED run is refused with `CALIBRATION_NOT_LOCKED_STATE`.
//
// That is a legitimate outcome, not an error state, and this screen renders it as one: the session
// survives as ordinary practice with whatever the registry still permits, nothing recorded is lost,
// and the words say exactly that. An empty panel or a red "failed" here would teach a coach that
// the app is flaky, which is how a deliberate refusal gets worked around.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE PRE-FLIGHT IS A MIRROR, NOT AN AUTHORITY
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `preflight()` re-implements `checkProtocolConformance()` on the client so an athlete learns
// BEFORE they bowl twenty-four balls that the phone is at 30 fps and this protocol needs 60. It
// reads the SAME facts the server reads (the 041 session row and its calibration row) with the SAME
// fail-closed reading `assembleCapture()` uses — an absent fps is 0, an unrecognised device class is
// LOW, an unrecognised eligibility is OFF, an unmeasured calibration check is UNKNOWN and never
// PASS. It cannot be more permissive than the server, and it never claims a run WILL be scored: the
// server re-checks everything and remains the only authority. A green pre-flight is "nothing known
// is wrong", never "this will count".
//
// NO NEW RUNTIME DEPENDENCY. The bars in the comparison are two divs and a width; a charting
// library for two points would be a hundred kilobytes to draw a rectangle.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal, request, type ApiResult } from '../../api/client';
import { getSession } from '../../api/practice';
import { PRACTICE_NAMESPACE as NS } from '../../config';
import { getState } from '../state';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 1 · THE CONTRACT, TRANSCRIBED
 *
 * These are the shapes `src/practice/standard-tests.ts` exports, narrowed to what this screen
 * reads. Unions that exist only on the backend (the 44 metric ids, the five measurement classes)
 * are `string` here: this client renders them, it never decides on them.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** The nine. Iterated rather than hand-written, exactly as the backend iterates its own copy. */
export const LOCKED_PARAMETERS = [
  'mode',
  'camera_role',
  'athlete_focus',
  'ball_type',
  'feed_type',
  'pitch_surface',
  'pitch_length_band',
  'environment',
  'metric_eligibility',
] as const;
export type LockedParameter = (typeof LOCKED_PARAMETERS)[number];

export type DeviceClass = 'LOW' | 'MID' | 'HIGH';
export type Eligibility = 'FULL' | 'LIMITED' | 'OFF';

/** The backend's own ordering. A MID phone does not satisfy a HIGH floor. */
export const DEVICE_RANK: Readonly<Record<DeviceClass, number>> = { LOW: 0, MID: 1, HIGH: 2 };

/** GET /v1/practice/standard-tests → protocols[] */
export interface LockedProtocol {
  id: string;
  protocolVersion: number;
  label: string;
  purpose: string;
  ballCounts: number[];
  locked: Record<LockedParameter, string>;
  cameraCount: number;
  minMeasuredFps: number;
  minDeviceClass: DeviceClass;
  restBetweenBallsSec: number;
  restBetweenBlocksSec: number;
  blockSize: number;
  /** What the test produces — every one permitted by a metric card under the locked capture. */
  emits: string[];
  /** What it declares it will NEVER produce. Named, never silently omitted. RPM lives here. */
  declaredRefusals: string[];
  primaryOutputs: string[];
}

export interface Catalogue {
  locked_parameters: string[];
  percentile_sufficiency: {
    MIN_DISTINCT_ATHLETES: number;
    MIN_DISTINCT_ORGS: number;
    MAX_SINGLE_ORG_SHARE: number;
    WINDOW_DAYS: number;
    MIN_MEASUREMENT_CLASS: string;
    FINE_RESOLUTION_ATHLETES: number;
    COARSE_BAND_WIDTH: number;
  };
  protocol_binding: { columns: string[]; required_ddl: string };
  protocols: LockedProtocol[];
}

export interface ConformanceMismatch {
  parameter: string;
  required: string | number;
  observed: string | number;
  why: string;
}

/** A run outside its lock. Carries NO outputs and NO evidence tier — there is no number on it. */
export interface StandardTestRunRefusal {
  scored: false;
  refused: true;
  test_id: string | null;
  protocol_version: number | null;
  reason: string;
  message: string;
  mismatches: ConformanceMismatch[];
  escalation: string | null;
}

export interface StandardTestOutput {
  metric_id: string;
  label: string;
  card_version: number;
  measurement_class: string;
  review_state: string;
  output_form: string;
  unit: string | null;
  value_num: number | null;
  value_low: number | null;
  value_high: number | null;
  value_category: string | null;
  caveats?: string[];
}

export interface StandardTestRefusedOutput {
  metric_id: string;
  label: string;
  refused: true;
  permanent: boolean;
  reason: string;
  message: string;
  escalation: string | null;
}

export interface StandardTestResult {
  scored: true;
  refused: false;
  test_id: string;
  test_label: string;
  protocol_version: number;
  athlete_id: string;
  session_id: string;
  ball_count: number;
  deliveries_total: number;
  deliveries_usable: number;
  completeness: 'COMPLETE' | 'PARTIAL';
  locked: Record<LockedParameter, string>;
  handedness: string;
  age_group: string;
  evidence_tier: string;
  outputs: StandardTestOutput[];
  refused_outputs: StandardTestRefusedOutput[];
  declared_refusals: StandardTestRefusedOutput[];
  comparability_key: string;
  interpretation: 'CONTEXT_NOT_VERDICT';
  disclaimer: string;
  observed_at: string;
}

/** A row of GET …/athletes/:id/standard-tests — a Passport record, or the run's refusal. */
export interface StandardTestPassportRecord {
  kind: 'STANDARD_TEST';
  test_id: string;
  test_label: string;
  protocol_version: number;
  evidence_tier: string;
  athlete_id: string;
  session_id: string;
  ball_count: number;
  completeness: 'COMPLETE' | 'PARTIAL';
  locked: Record<LockedParameter, string>;
  comparability_key: string;
  outputs: StandardTestOutput[];
  withheld: Array<{ metric_id: string; label: string; permanent: boolean; message: string }>;
  interpretation: 'CONTEXT_NOT_VERDICT';
  disclaimer: string;
  observed_at: string;
}

export interface ComparabilityVerdict {
  comparable: boolean;
  key: string | null;
  mismatches: ConformanceMismatch[];
  message: string;
}

/** Carries no `percentile`, no `percentile_band`, no `rank` — not even set to null. */
export interface PercentileRefusal {
  available: false;
  refused: true;
  reason: string;
  message: string;
  scope: string;
  metric_id: string;
  age_band: string | null;
  shortfall: Record<string, { have: number; need: number }>;
  excluded: Record<string, number>;
}

export interface PercentileContext {
  available: true;
  refused: false;
  metric_id: string;
  card_version: number;
  age_band: string;
  scope: string;
  comparability_key: string;
  percentile_band: [number, number];
  percentile_band_label: string;
  /** ONLY at 1000+ athletes. Its ABSENCE below that is the point, so it is read, never defaulted. */
  percentile?: number;
  population: {
    athletes: number;
    orgs: number;
    largest_org_share: number;
    window_days: number;
    min_measurement_class: string;
  };
  interpretation: 'CONTEXT_NOT_TALENT_VERDICT';
  caveats: string[];
}

/* ── The three type guards. Each tests the shape's OWN discriminator pair, so a truncated or
 *    unfamiliar body can never fall through into the "we have a number" branch. ─────────────── */

/** `null` and non-objects are answered before any property is read. */
function rec(x: unknown): Record<string, unknown> {
  return x !== null && typeof x === 'object' ? (x as Record<string, unknown>) : {};
}

export function isRunRefusal(x: unknown): x is StandardTestRunRefusal {
  const v = rec(x);
  return v['refused'] === true && v['scored'] === false;
}

export function isScoredResult(x: unknown): x is StandardTestResult {
  const v = rec(x);
  return v['scored'] === true && v['refused'] === false;
}

export function isPercentileRefusal(x: unknown): x is PercentileRefusal {
  const v = rec(x);
  return v['available'] === false && v['refused'] === true;
}

export function isPercentileContext(x: unknown): x is PercentileContext {
  const v = rec(x);
  return v['available'] === true && v['refused'] === false;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 2 · THE SIX READS
 *
 * Every route in this lane is a READ; the lane owns no table and writes nothing. The subject is
 * always a PATH parameter and the actor is the bearer principal — no body here carries identity,
 * and in particular the age band and the date of birth are NOT sent on the percentile call. The
 * service derives both from the athlete row, which is the difference between a gate and a hint.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export function fetchCatalogue(): Promise<ApiResult<Catalogue>> {
  return request(`${NS}/standard-tests`);
}

export function fetchProtocol(testId: string): Promise<ApiResult<{ protocol: LockedProtocol }>> {
  return request(`${NS}/standard-tests/${encodeURIComponent(testId)}`);
}

export function fetchRun(
  sessionId: string,
): Promise<ApiResult<{ result: StandardTestResult | StandardTestRunRefusal; passport_record: StandardTestPassportRecord | null }>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/standard-test`);
}

export function fetchAthleteTests(
  athleteId: string,
): Promise<ApiResult<{ records: Array<StandardTestPassportRecord | StandardTestRunRefusal> }>> {
  return request(`${NS}/athletes/${encodeURIComponent(athleteId)}/standard-tests`);
}

export function fetchComparison(
  sessionId: string,
  otherSessionId: string,
): Promise<ApiResult<{
  verdict: ComparabilityVerdict;
  a: StandardTestResult | StandardTestRunRefusal;
  b: StandardTestResult | StandardTestRunRefusal;
}>> {
  return request(
    `${NS}/sessions/${encodeURIComponent(sessionId)}/standard-test/compare/${encodeURIComponent(otherSessionId)}`,
  );
}

/**
 * GET …/standard-test/percentile?metric_id=&scope=
 *
 * `scope` is typed as the literal pair rather than `string` so this client cannot express a third
 * value. The server resolves anything that is not exactly 'public' to PRIVATE — a typo can only
 * narrow the audience — but a client that can only send two values never tests that mercy.
 */
export function fetchPercentile(
  sessionId: string,
  metricId: string,
  scope: 'private' | 'public',
): Promise<ApiResult<{ percentile: PercentileContext | PercentileRefusal | StandardTestRunRefusal; scope: string }>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/standard-test/percentile`, {
    query: { metric_id: metricId, scope },
  });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 3 · PRE-FLIGHT — does THIS phone, in THIS state, meet THIS protocol's floor?
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface ObservedSetup {
  /** Measured, not nominal. `null` means nobody has measured it — which is not a pass. */
  measured_fps: number | null;
  device_class: DeviceClass | null;
  camera_count: number | null;
  metric_eligibility: Eligibility | null;
  /** The 041 session columns, as they stand. A missing column is UNKNOWN, never the locked value. */
  locked: Partial<Record<LockedParameter, string | null>>;
}

export interface PreflightFailure {
  /** The locked parameter or capture floor that is not met. Named, so it is actionable. */
  requirement: string;
  required: string;
  observed: string;
  why: string;
}

export interface PreflightVerdict {
  protocol_id: string;
  protocol_label: string;
  /** True only when NOTHING known is short of the lock. Never a promise the run will be scored. */
  ok: boolean;
  failures: PreflightFailure[];
  /** LIMITED or OFF: there is no Standard Test at all, and the session is ordinary practice. */
  calibration_blocks_test: boolean;
}

/**
 * THE CLIENT-SIDE MIRROR of `checkProtocolConformance()`.
 *
 * Iterating `LOCKED_PARAMETERS` rather than writing nine comparisons is the backend's own choice and
 * it is copied for the backend's own reason: a parameter added to a protocol without being added to
 * that list would otherwise be silently unenforced — the control whose removal changes nothing.
 *
 * Every unknown is a FAILURE, never a pass. That is stricter than nothing and never laxer than the
 * server: `assembleCapture()` resolves an absent fps to 0, an unrecognised device class to LOW and
 * an unrecognised eligibility to OFF, all of which fail the same checks there.
 */
export function preflight(
  p: LockedProtocol,
  observed: ObservedSetup,
  ballCount?: number | null,
): PreflightVerdict {
  const failures: PreflightFailure[] = [];

  for (const key of LOCKED_PARAMETERS) {
    const required = String(p.locked[key]);
    const raw = observed.locked[key];
    const seen = raw === null || raw === undefined || raw === '' ? 'UNKNOWN' : String(raw);
    if (seen !== required) {
      failures.push({
        requirement: key,
        required,
        observed: seen === 'UNKNOWN' ? 'not established' : seen,
        why: key === 'metric_eligibility'
          ? 'a Standard Test locks the capture-quality state EXACTLY; a run at another state is a '
            + 'different measurement, not a weaker one'
          : `${p.label} locks ${key}; a run with a different value is not this test`,
      });
    }
  }

  // The capture floor. An unmeasured fps is 0 here for the same reason it is 0 on the server.
  const fps = observed.measured_fps ?? 0;
  if (!(fps >= p.minMeasuredFps)) {
    failures.push({
      requirement: 'measured_fps',
      required: `${p.minMeasuredFps} fps or more, measured`,
      observed: observed.measured_fps === null ? 'not measured' : `${observed.measured_fps} fps`,
      why: 'below the protocol floor the events this test times fall between frames',
    });
  }

  const cls = observed.device_class ?? 'LOW';
  if (DEVICE_RANK[cls] < DEVICE_RANK[p.minDeviceClass]) {
    failures.push({
      requirement: 'device_class',
      required: `${p.minDeviceClass} or better`,
      observed: observed.device_class === null ? 'not established (read as LOW)' : observed.device_class,
      why: 'the protocol floor is a device capability, not a preference — a slower phone cannot be '
        + 'configured into meeting it',
    });
  }

  // Matching the server exactly: an ABSENT camera count is read as the protocol's own, so a client
  // that has not counted cameras does not fail a check the server would have passed.
  const cams = observed.camera_count ?? p.cameraCount;
  if (cams !== p.cameraCount) {
    failures.push({
      requirement: 'camera_count',
      required: String(p.cameraCount),
      observed: String(cams),
      why: 'a second viewpoint changes what is observable; it is part of the locked setup',
    });
  }

  if (ballCount !== null && ballCount !== undefined && !p.ballCounts.includes(ballCount)) {
    failures.push({
      requirement: 'ball_count',
      required: p.ballCounts.join(' or '),
      observed: String(ballCount),
      why: 'the block size is part of the protocol — a longer or shorter block measures something else',
    });
  }

  return {
    protocol_id: p.id,
    protocol_label: p.label,
    ok: failures.length === 0,
    failures,
    calibration_blocks_test: observed.metric_eligibility !== p.locked.metric_eligibility,
  };
}

/**
 * Read the observed setup off a 041 session row and its calibration rows.
 *
 * A FAITHFUL TRANSCRIPTION of `assembleCapture()`, including which calibration row is chosen (the
 * one whose camera role matches the session's, else the first) and where the numbers live. It is a
 * transcription and not an invention: no fact here is derived from anything but a column the server
 * reads for the same purpose. Where the app holds no fact, the field is `null` and `preflight()`
 * fails the requirement rather than guessing.
 */
export function observedFromSession(
  session: Record<string, unknown> | null | undefined,
  calibrations: unknown[] | null | undefined,
): ObservedSetup {
  const s = (session ?? {}) as Record<string, unknown>;
  const rows = ((calibrations ?? []) as Array<Record<string, unknown>>);
  const chosen = rows.find((c) => String(c['camera_role']) === String(s['camera_role'])) ?? rows[0] ?? null;

  const device = (s['device_json'] ?? {}) as Record<string, unknown>;
  const rawClass = String(device['device_class'] ?? '');
  const rawElig = String(s['metric_eligibility'] ?? '');

  const fpsRaw = chosen?.['measured_fps'] ?? s['device_fps_measured'] ?? null;
  const fps = fpsRaw === null || fpsRaw === undefined ? null : Number(fpsRaw);

  const locked: Partial<Record<LockedParameter, string | null>> = {};
  for (const k of LOCKED_PARAMETERS) {
    const v = s[k];
    locked[k] = v === null || v === undefined ? null : String(v);
  }

  const camsRaw = s['camera_count'] ?? chosen?.['camera_count'] ?? null;

  return {
    measured_fps: fps !== null && Number.isFinite(fps) ? fps : null,
    device_class: (['LOW', 'MID', 'HIGH'] as const).includes(rawClass as DeviceClass)
      ? (rawClass as DeviceClass)
      : null,
    camera_count: camsRaw === null || camsRaw === undefined ? null : Number(camsRaw),
    metric_eligibility: (['FULL', 'LIMITED', 'OFF'] as const).includes(rawElig as Eligibility)
      ? (rawElig as Eligibility)
      : null,
    locked,
  };
}

/** The sentence the LIMITED case must say, in one place so it cannot drift between two panels. */
export const NO_TEST_AT_LIMITED =
  'At LIMITED calibration there is no Standard Test. Nothing is broken and nothing is lost: this '
  + 'block is still recorded as ordinary practice, and every metric the registry still permits under '
  + 'this capture is still measured. What it cannot be is a Standard Test result, because all five '
  + 'protocols lock the capture-quality state to FULL exactly — a result taken at a different state '
  + 'is not comparable with one taken at FULL, and a number that cannot be compared is the one thing '
  + 'a Standard Test must never produce.';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 4 · VIEWS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** One protocol: what it measures, what it refuses to measure, and what the setup must be. */
export function protocolCard(p: LockedProtocol, expanded: boolean, onToggle?: () => void): HTMLElement {
  const head = el('button', {
    class: `row-item row-tap st-protocol${expanded ? ' row-selected' : ''}`,
    'data-protocol': p.id,
    ...(onToggle ? { onclick: onToggle } : {}),
  },
    el('p', {}, `${p.label} — ${p.ballCounts.join(' or ')} balls`),
    el('span', { class: 'tag' }, `v${p.protocolVersion}`),
  );

  if (!expanded) return head;

  const detail = el('div', { class: 'st-protocol-detail' },
    el('p', { class: 'note' }, p.purpose),

    el('h3', {}, 'What it measures'),
    el('div', { class: 'list' }, ...p.primaryOutputs.map((o) => el('p', { class: 'st-output' }, o))),
    el('p', { class: 'note' }, `Metric cards asked: ${p.emits.join(', ')}.`),

    // NAMED, not omitted. A metric that is simply absent is indistinguishable from one nobody has
    // built yet, and "not built yet" invites building it.
    el('h3', {}, 'What it refuses to measure'),
    el('div', { class: 'list' },
      ...p.declaredRefusals.map((m) => el('p', { class: 'st-declared-refusal' }, m))),
    el('p', { class: 'note' },
      'These are not gaps. Each one is a metric card that refuses under this capture — permanently, '
      + 'or until a setup the card names exists. The refusal comes from the card, not from this app.'),

    el('h3', {}, 'What the phone and the setup must be'),
    field('Cameras', p.cameraCount),
    field('Measured frame rate, minimum', `${p.minMeasuredFps} fps`),
    field('Device class, minimum', p.minDeviceClass),
    field('Calibration state (exact, not a floor)', p.locked.metric_eligibility),
    field('Block size', `${p.blockSize} balls`),
    field('Rest between balls', `${p.restBetweenBallsSec} s`),
    field('Rest between blocks', `${p.restBetweenBlocksSec} s`),
    el('p', { class: 'note' },
      'Rest is part of the test. A twelve-ball speed block bowled with twenty seconds between balls '
      + 'measures fatigue, not effort.'),

    el('h3', {}, 'The nine locked parameters'),
    el('div', { class: 'list' },
      ...LOCKED_PARAMETERS.map((k) => field(k, p.locked[k]))),
    el('p', { class: 'note' },
      'Change one of these and it is a different test. That is why a change is a protocol version '
      + 'bump and never an edit in place — an old result keeps pointing at the rules that applied to it.'),
  );

  return el('div', { class: 'st-protocol-block' }, head, detail);
}

/** The pre-flight verdict, with every failing requirement named. */
export function preflightView(v: PreflightVerdict, p: LockedProtocol): HTMLElement {
  if (v.ok) {
    return el('div', { class: 'st-preflight st-preflight-ok', 'data-protocol': v.protocol_id },
      el('p', { class: 'ok' }, `Nothing known is short of ${v.protocol_label} v${p.protocolVersion}.`),
      el('p', { class: 'note' },
        'This is a mirror of the server\'s own conformance check against the facts recorded for this '
        + 'session. It is not a promise that the run will be scored: the server re-checks every '
        + 'parameter, and the usable-ball count is only known after the block is recorded.'),
    );
  }

  const calibrationBlocks = v.calibration_blocks_test;
  const box = el('div', {
    class: `st-preflight st-preflight-failed${calibrationBlocks ? ' st-preflight-limited' : ''}`,
    'data-protocol': v.protocol_id,
    role: 'alert',
  });

  box.append(
    el('p', { class: 'st-preflight-headline' },
      `This setup does not meet ${v.protocol_label}, so it would not be a Standard Test.`),
  );

  if (calibrationBlocks) {
    // The most common refusal, and the one most likely to be read as a fault. It gets the sentence.
    box.append(el('p', { class: 'st-limited-note' }, NO_TEST_AT_LIMITED));
  }

  box.append(el('div', { class: 'list st-failures' }, ...v.failures.map((f) => el('div', {
    class: 'row-item st-failure',
    'data-requirement': f.requirement,
  },
    el('p', { class: 'st-failure-requirement' }, f.requirement),
    field('Required', f.required),
    field('This setup', f.observed),
    el('p', { class: 'note' }, f.why),
  ))));

  box.append(el('p', { class: 'note' },
    'The session is still kept. A block recorded outside a protocol is ordinary practice, which is a '
    + 'real thing to have — it is simply not this test.'));
  return box;
}

/** A run the server refused. There is no number on this shape, so there is none in this view. */
export function runRefusalView(r: StandardTestRunRefusal): HTMLElement {
  const box = el('div', {
    class: `st-run-refusal st-reason-${String(r.reason).toLowerCase()}`,
    'data-reason': r.reason,
    role: 'alert',
  },
    el('p', { class: 'st-refusal-headline' }, 'Not a Standard Test result'),
    // The server's own sentence. Verbatim — it names every mismatch and says why it matters.
    el('p', { class: 'st-refusal-message' }, r.message),
  );

  if (r.reason === 'CALIBRATION_NOT_LOCKED_STATE') {
    box.append(el('p', { class: 'st-limited-note' }, NO_TEST_AT_LIMITED));
  }

  if (r.mismatches.length) {
    box.append(el('div', { class: 'list st-mismatches' }, ...r.mismatches.map((m) => el('div', {
      class: 'row-item st-mismatch',
      'data-parameter': m.parameter,
    },
      el('p', { class: 'st-mismatch-parameter' }, m.parameter),
      field('Locked', m.required),
      field('This run', m.observed),
      el('p', { class: 'note' }, m.why),
    ))));
  }
  if (r.escalation) box.append(el('p', { class: 'st-escalation' }, r.escalation));
  return box;
}

function outputRow(o: StandardTestOutput): HTMLElement {
  // A range metric has no point value ON PURPOSE — `bowl_speed_range`'s card forbids one. So the
  // value is read from whichever field the output form actually filled, and a missing one prints as
  // the em dash `field()` uses rather than as a zero.
  const value = o.value_num !== null && o.value_num !== undefined
    ? String(o.value_num)
    : (o.value_low !== null && o.value_high !== null && o.value_low !== undefined && o.value_high !== undefined
      ? `${o.value_low} – ${o.value_high}`
      : (o.value_category ?? null));

  return el('div', { class: 'row-item st-output-row', 'data-metric': o.metric_id },
    el('p', {}, o.label),
    field('Value', value === null ? null : `${value}${o.unit ? ` ${o.unit}` : ''}`),
    field('Output form', o.output_form),
    field('Measurement class', o.measurement_class),
    field('Reviewed', o.review_state),
    field('Metric card version', o.card_version),
    ...(o.caveats ?? []).map((c) => el('p', { class: 'note' }, c)),
  );
}

/** A scored run. */
export function resultView(r: StandardTestResult): HTMLElement {
  return el('div', { class: 'st-result', 'data-session': r.session_id, 'data-test': r.test_id },
    field('Test', `${r.test_label} v${r.protocol_version}`),
    field('Balls', r.ball_count),
    field('Deliveries usable', `${r.deliveries_usable} of ${r.deliveries_total}`),
    field('Completeness', r.completeness),
    field('Evidence tier', r.evidence_tier),
    field('Handedness', r.handedness),
    field('Recorded', r.observed_at),

    r.completeness === 'PARTIAL' && el('p', { class: 'warn' },
      'PARTIAL: at least one of this protocol\'s outputs was not produced. A partial result is never '
      + 'compared against another run and never enters a percentile population.'),

    el('h3', {}, 'Outputs'),
    r.outputs.length
      ? el('div', { class: 'list' }, ...r.outputs.map(outputRow))
      : empty('This run produced no outputs.'),

    r.refused_outputs.length > 0 && el('h3', {}, 'Not produced on this run'),
    ...r.refused_outputs.map((x) => el('div', { class: 'row-item st-refused-output', 'data-metric': x.metric_id },
      el('p', {}, x.label), el('p', { class: 'note' }, x.message))),

    el('h3', {}, 'Declared never produced'),
    ...r.declared_refusals.map((x) => el('div', { class: 'row-item st-declared-output', 'data-metric': x.metric_id },
      el('p', {}, x.label),
      el('span', { class: 'tag' }, x.permanent ? 'permanent' : 'not under this capture'),
      // The metric card's own words, so what the athlete reads cannot drift from the card.
      el('p', { class: 'note' }, x.message),
      x.escalation && el('p', { class: 'note' }, x.escalation))),

    el('h3', {}, 'Comparability'),
    el('p', { class: 'mono small st-comparability-key' }, r.comparability_key),
    el('p', { class: 'note' },
      'Another run is comparable with this one only if that entire key matches — protocol and its '
      + 'version, ball count, all nine locked parameters and handedness — and only if the metric card '
      + 'version of every shared output matches too.'),
    el('p', { class: 'note' }, r.disclaimer),
  );
}

/**
 * THE ONLY FUNCTION IN THIS FILE THAT PUTS TWO RUNS ON ONE AXIS.
 *
 * It returns `null` for anything the server did not call comparable. The guard is not a wrapper
 * around drawing code that also exists elsewhere — the drawing code is only in here, so there is no
 * second route to a shared axis and no `if` for a future edit to invert.
 */
export function sharedAxisChart(
  verdict: ComparabilityVerdict,
  a: StandardTestResult | StandardTestRunRefusal,
  b: StandardTestResult | StandardTestRunRefusal,
): HTMLElement | null {
  if (verdict.comparable !== true) return null;
  if (!isScoredResult(a) || !isScoredResult(b)) return null;
  // Belt and braces against a server that says comparable while the keys differ: the key IS the
  // comparability claim, so disagreeing with it is a reason to draw nothing.
  if (a.comparability_key !== b.comparability_key) return null;

  const bByMetric = new Map(b.outputs.map((o) => [o.metric_id, o]));
  const charts: HTMLElement[] = [];

  for (const oa of a.outputs) {
    const ob = bByMetric.get(oa.metric_id);
    if (!ob) continue;
    if (oa.value_num === null || oa.value_num === undefined) continue;
    if (ob.value_num === null || ob.value_num === undefined) continue;

    const hi = Math.max(oa.value_num, ob.value_num, 0);
    const span = hi === 0 ? 1 : hi;
    const bar = (o: StandardTestOutput, sessionId: string): HTMLElement =>
      el('div', { class: 'st-series', 'data-session': sessionId },
        el('span', { class: 'st-series-label' }, sessionId),
        el('div', {
          class: 'st-bar',
          style: `width:${Math.round(((o.value_num as number) / span) * 100)}%`,
        }),
        el('span', { class: 'st-series-value' }, `${o.value_num}${o.unit ? ` ${o.unit}` : ''}`));

    charts.push(el('div', {
      class: 'st-chart',
      'data-metric': oa.metric_id,
    },
      el('p', { class: 'st-chart-title' }, oa.label),
      el('p', { class: 'st-axis', 'data-axis-max': String(hi) },
        `Axis: 0 – ${hi}${oa.unit ? ` ${oa.unit}` : ''}`),
      bar(oa, a.session_id),
      bar(ob, b.session_id),
    ));
  }

  if (!charts.length) return null;
  return el('div', { class: 'st-charts' },
    el('p', { class: 'note' }, verdict.message),
    ...charts);
}

/** The comparison panel: a chart only where the server said the pair is comparable. */
export function comparisonView(
  verdict: ComparabilityVerdict,
  a: StandardTestResult | StandardTestRunRefusal,
  b: StandardTestResult | StandardTestRunRefusal,
): HTMLElement {
  const chart = sharedAxisChart(verdict, a, b);
  if (chart) return el('div', { class: 'st-comparison st-comparison-comparable' }, chart);

  const box = el('div', { class: 'st-comparison st-comparison-refused', role: 'alert' },
    el('p', { class: 'st-refusal-headline' }, 'These two runs are not comparable'),
    // The server's sentence, which already names every mismatch.
    el('p', { class: 'st-refusal-message' }, verdict.message),
  );
  if (verdict.mismatches.length) {
    box.append(el('div', { class: 'list st-mismatches' }, ...verdict.mismatches.map((m) => el('div', {
      class: 'row-item st-mismatch',
      'data-parameter': m.parameter,
    },
      el('p', { class: 'st-mismatch-parameter' }, m.parameter),
      field('First run', m.required),
      field('Second run', m.observed),
      el('p', { class: 'note' }, m.why),
    ))));
  }
  box.append(el('p', { class: 'note' },
    'There is no close-enough, no adjustment factor and no confidence-weighted comparison. Those all '
    + 'end with two numbers side by side and a footnote nobody reads, so no chart is drawn at all.'));
  return box;
}

/**
 * A percentile, or the refusal with its exact shortfall.
 *
 * THE REFUSAL BRANCH CANNOT RENDER A POSITION. `PercentileRefusal` carries no `percentile`, no
 * `percentile_band` and no `rank` — not even set to null — so there is no field on it to read. What
 * it does carry is `have` against `need`, and that is what goes on screen: "not yet" with a number
 * attached is actionable, and a spinner in its place would be a lie about what the app is doing.
 */
export function percentileView(p: PercentileContext | PercentileRefusal | StandardTestRunRefusal): HTMLElement {
  if (isRunRefusal(p)) return runRefusalView(p);

  if (isPercentileRefusal(p)) {
    const box = el('div', {
      class: `st-percentile st-percentile-refused st-reason-${String(p.reason).toLowerCase()}`,
      'data-reason': p.reason,
      role: 'alert',
    },
      el('p', { class: 'st-refusal-headline' }, 'No percentile'),
      el('p', { class: 'st-refusal-message' }, p.message),
      field('Metric', p.metric_id),
      field('Age band', p.age_band),
      field('Scope asked for', p.scope),
    );

    const shortfall = Object.entries(p.shortfall ?? {});
    if (shortfall.length) {
      box.append(
        el('h3', {}, 'The actual shortfall'),
        el('div', { class: 'list st-shortfall' }, ...shortfall.map(([k, v]) => el('div', {
          class: 'row-item st-shortfall-row',
          'data-shortfall': k,
        },
          el('p', {}, k.replace(/_/g, ' ')),
          el('p', { class: 'st-have-need' }, `have ${v.have}, need ${v.need}`),
        ))),
      );
    }

    const excluded = Object.entries(p.excluded ?? {}).filter(([, n]) => n > 0);
    if (excluded.length) {
      box.append(
        el('h3', {}, 'Why comparable results were excluded'),
        el('p', { class: 'note' },
          'Excluded, not adjusted. A result taken under a different setup is not brought into line — '
          + 'it is dropped, and counted here so "insufficient" is never mysterious.'),
        el('div', { class: 'list' }, ...excluded.map(([k, n]) => field(k.replace(/_/g, ' '), n))),
      );
    }
    return box;
  }

  if (!isPercentileContext(p)) {
    // An unrecognised body is shown as itself rather than coerced into a position.
    return el('div', { class: 'st-percentile st-percentile-unrecognised', role: 'alert' },
      el('p', { class: 'st-refusal-headline' }, 'Unrecognised percentile response'),
      el('pre', {}, JSON.stringify(p, null, 2)));
  }

  const box = el('div', { class: 'st-percentile st-percentile-available', 'data-metric': p.metric_id },
    el('p', { class: 'st-percentile-band' }, `Band ${p.percentile_band_label}`),
    field('Metric', p.metric_id),
    field('Age band', p.age_band),
    field('Scope', p.scope),
  );

  // Present ONLY at 1000+ athletes. Read, never defaulted: `?? band` would manufacture exactly the
  // precision the absence of this field exists to withhold.
  if (typeof p.percentile === 'number') {
    box.append(el('p', { class: 'st-percentile-point' }, `${p.percentile}th percentile`));
  } else {
    // No `?? band` and no rounding of the band into a point: the ABSENCE of `percentile` is the
    // server saying the data cannot support one, and the caveats below carry its own reason and its
    // own numbers, so nothing is restated here that could drift from them.
    box.append(el('p', { class: 'note' },
      'No single percentile point is reported for this population — only the band of ten above. See '
      + 'the population and the caveats below for the server\'s own reason.'));
  }

  box.append(
    el('h3', {}, 'The population behind it'),
    field('Athletes', p.population.athletes),
    field('Organisations', p.population.orgs),
    field('Largest single organisation share', p.population.largest_org_share),
    field('Window', `${p.population.window_days} days`),
    field('Minimum measurement class', p.population.min_measurement_class),
    el('p', { class: 'st-interpretation' },
      'This is CONTEXT, not a talent verdict. It says where this result sits among comparable '
      + 'results — nothing about potential, ceiling or selection.'),
    ...p.caveats.map((c) => el('p', { class: 'note' }, c)),
  );
  return box;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 5 · THE SCREEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Screen-local selection. Deliberately NOT in `ui/state.ts`: none of it is an identity, a session
 * or an athlete, and the one rule that module enforces (an athlete id only ever comes from a server
 * response) has nothing to do with which protocol a coach has open. Every id stored here came out
 * of a response body — the compare target is picked from the server's own history list, never typed.
 */
const view: {
  protocolId: string | null;
  compareWith: string | null;
  metricId: string | null;
  scope: 'private' | 'public';
} = { protocolId: null, compareWith: null, metricId: null, scope: 'private' };

/** Test seam: a fresh screen between cases, with no leaked selection. */
export function __resetStandardTestsView(): void {
  view.protocolId = null;
  view.compareWith = null;
  view.metricId = null;
  view.scope = 'private';
}

/**
 * PUBLIC SCOPE IS OFFERED ONLY FOR AN ATHLETE KNOWN NOT TO BE A MINOR.
 *
 * Fail-closed on unknown: `subjectIsMinor` is `boolean | null`, and only the literal `false` opens
 * the public option. The server refuses a public percentile for a minor before it counts a single
 * population member, so this is not the gate — it is this client declining to compose a request
 * whose only possible answer is a refusal about a child.
 */
export function publicScopeAllowed(subjectIsMinor: boolean | null | undefined): boolean {
  return subjectIsMinor === false;
}

export async function standardTestsScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  mount(root, spinner('Loading the Standard Test protocols…'));

  const catalogue = await fetchCatalogue();
  const out = el('div');

  /* ── THE CATALOGUE ────────────────────────────────────────────────────────────────────────
   * Published so an athlete can read what a test demands BEFORE they set the phone up, which is
   * the only way a locked protocol is fair. */
  if (isRefusal(catalogue)) {
    out.append(section('Standard Tests', refusalView(catalogue, { onRetry: rerender })));
    mount(root, out);
    return;
  }

  const cat = catalogue.value;
  const protocols = cat.protocols ?? [];
  out.append(section('The five Standard Tests',
    el('p', { class: 'note' },
      'A Standard Test locks the camera role, the capture setup, the ball, the pitch, the block size '
      + 'and the rest timing, so that a result recorded in March and one recorded in November are '
      + 'the same measurement of the same thing. A run outside its lock is refused — not scored at a '
      + 'lower confidence, not scored with a caveat. It produces no score at all.'),
    protocols.length
      ? el('div', { class: 'list' }, ...protocols.map((p) => protocolCard(
          p,
          view.protocolId === p.id,
          () => { view.protocolId = view.protocolId === p.id ? null : p.id; rerender(); },
        )))
      : empty('This build of the server published no protocols.'),
  ));

  // The binding this lane reads is a column migration 041 does not carry. The backend surfaces that
  // rather than hiding it, so this screen does too: without it, every run refuses NO_PROTOCOL_BINDING
  // and a tester would otherwise hunt the phone for a fault that is in the schema.
  if ((cat.protocol_binding?.columns ?? []).length) {
    out.append(section('How a session becomes a test run',
      el('p', { class: 'note' },
        'A session is a Standard Test run only if it was DECLARED one, on the row, before it was '
        + 'recorded. The server records that on three columns:'),
      el('p', { class: 'mono small' }, cat.protocol_binding.columns.join(', ')),
      el('p', { class: 'note' },
        'Where those columns are absent every run refuses with NO_PROTOCOL_BINDING, and that is a '
        + 'schema gap on the backend, not a fault on this phone. The server states the migration it '
        + 'needs rather than squatting the binding into a JSON bag.'),
      el('details', {}, el('summary', {}, 'The DDL the server asks for'),
        el('pre', {}, cat.protocol_binding.required_ddl)),
    ));
  }

  if (!st.athlete) {
    out.append(section('Your runs',
      empty('No athlete selected. Pick one on the Coach screen or the Parent screen — this app only '
        + 'ever uses an athlete id the server handed it.')));
    mount(root, out);
    return;
  }

  const athleteId = st.athlete.athleteId;

  /* ── PRE-FLIGHT ─────────────────────────────────────────────────────────────────────────── */
  out.append(await preflightSection(protocols, rerender));

  /* ── HISTORY ────────────────────────────────────────────────────────────────────────────── */
  const history = await fetchAthleteTests(athleteId);
  const scored: StandardTestPassportRecord[] = [];
  if (isRefusal(history)) {
    out.append(section('Test history', refusalView(history, { onRetry: rerender })));
  } else {
    const records = history.value.records ?? [];
    for (const r of records) if (!isRunRefusal(r)) scored.push(r as StandardTestPassportRecord);
    out.append(section('Test history',
      el('p', { class: 'note' },
        'Only sessions declared as Standard Test runs appear here. Ordinary practice is not listed as '
        + 'a failed test — it was never a test. A run that did not conform appears as its refusal, so '
        + 'this list cannot quietly omit the ones that did not count.'),
      records.length
        ? el('div', { class: 'list' }, ...records.map(historyRow))
        : empty('No session for this athlete has been declared a Standard Test run.'),
    ));
  }

  /* ── THIS SESSION'S RESULT, ITS COMPARISON AND ITS PERCENTILE ───────────────────────────── */
  if (!st.sessionId) {
    out.append(section('This session',
      empty('No session bound. Bind one on the Coach screen to see whether it was a Standard Test '
        + 'run, and what it produced.')));
    mount(root, out);
    return;
  }

  const run = await fetchRun(st.sessionId);
  if (isRefusal(run)) {
    out.append(section('This session', refusalView(run, { onRetry: rerender })));
    mount(root, out);
    return;
  }

  const result = run.value.result;
  out.append(section('This session',
    isRunRefusal(result) ? runRefusalView(result) : resultView(result)));

  if (!isScoredResult(result)) {
    mount(root, out);
    return;
  }

  out.append(await comparisonSection(result, scored, rerender));
  out.append(await percentileSection(result, rerender));
  mount(root, out);
}

/* ── the sections ──────────────────────────────────────────────────────────────────────────── */

async function preflightSection(
  protocols: LockedProtocol[],
  rerender: () => void,
): Promise<HTMLElement> {
  const st = getState();
  const chosen = protocols.find((p) => p.id === view.protocolId) ?? null;

  const head = el('p', { class: 'note' },
    'Before the block, not after it. This checks the facts recorded for the bound session against '
    + 'the protocol\'s lock, so nobody bowls twenty-four balls and then learns the phone was at 30 fps. '
    + 'Anything not measured counts as not met — an unmeasured frame rate is never read as a pass.');

  if (!chosen) {
    return section('Pre-flight', head,
      empty('Open a protocol above to check this session against it.'));
  }
  if (!st.sessionId) {
    return section('Pre-flight', head,
      empty(`Bind a session on the Coach screen to check it against ${chosen.label}. Without a session `
        + 'there is no measured frame rate, no device class and no calibration state to check, and '
        + 'this screen will not invent one.'));
  }

  const s = await getSession(st.sessionId);
  if (isRefusal(s)) return section('Pre-flight', head, refusalView(s, { onRetry: rerender }));

  const observed = observedFromSession(
    s.value.session as unknown as Record<string, unknown>,
    s.value.calibrations,
  );
  return section(`Pre-flight — ${chosen.label}`, head, preflightView(preflight(chosen, observed), chosen));
}

function historyRow(r: StandardTestPassportRecord | StandardTestRunRefusal): HTMLElement {
  if (isRunRefusal(r)) {
    return el('div', { class: 'row-item st-history-refused', 'data-reason': r.reason },
      el('p', {}, r.test_id ? `${r.test_id} — refused` : 'A declared run that was refused'),
      el('p', { class: 'st-refusal-message' }, r.message),
      // Stated rather than papered over: this row cannot be opened or compared, because the
      // history endpoint does not carry a session id on a refusal. See the lane report.
      el('p', { class: 'note' },
        'This run cannot be opened from here: the history endpoint does not carry a session id on a '
        + 'refused run, and this app will not guess one.'));
  }
  return el('div', { class: 'row-item st-history-row', 'data-session': r.session_id },
    el('p', {}, `${r.test_label} v${r.protocol_version} — ${r.ball_count} balls`),
    el('p', { class: 'mono small' }, r.session_id),
    field('Completeness', r.completeness),
    field('Evidence tier', r.evidence_tier),
    field('Recorded', r.observed_at),
    r.withheld.length > 0 && field('Withheld outputs', r.withheld.length));
}

async function comparisonSection(
  result: StandardTestResult,
  scored: StandardTestPassportRecord[],
  rerender: () => void,
): Promise<HTMLElement> {
  const others = scored.filter((r) => r.session_id !== result.session_id);
  const head = el('p', { class: 'note' },
    'Two runs are comparable only if the protocol version, the ball count and all nine locked '
    + 'parameters match — plus handedness and the metric card version of every shared output. Where '
    + 'they do not, no chart is drawn: two incomparable numbers on one axis is a lie with a picture '
    + 'on it.');

  if (!others.length) {
    return section('Compare with another run', head,
      empty('There is no other scored Standard Test run for this athlete to compare against.'));
  }

  const select = el('select', { class: 'input' },
    el('option', { value: '' }, 'choose a run…'),
    ...others.map((r) => el('option', {
      value: r.session_id,
      ...(view.compareWith === r.session_id ? { selected: true } : {}),
    }, `${r.test_label} v${r.protocol_version} · ${r.ball_count} balls · ${r.observed_at}`)));
  select.addEventListener('change', () => {
    view.compareWith = select.value || null;
    rerender();
  });

  const body = el('div');
  const wrap = section('Compare with another run', head, select, body);

  if (!view.compareWith) {
    body.append(empty('No second run chosen.'));
    return wrap;
  }

  const cmp = await fetchComparison(result.session_id, view.compareWith);
  if (isRefusal(cmp)) {
    body.append(refusalView(cmp, { onRetry: rerender }));
    return wrap;
  }
  body.append(comparisonView(cmp.value.verdict, cmp.value.a, cmp.value.b));
  return wrap;
}

async function percentileSection(
  result: StandardTestResult,
  rerender: () => void,
): Promise<HTMLElement> {
  const st = getState();
  const numeric = result.outputs.filter((o) => o.value_num !== null && o.value_num !== undefined);

  const head = el('p', { class: 'note' },
    'A percentile is context, never a talent verdict, and it is gated hard. Below 100 athletes in a '
    + 'band one percentile point is less than one person, so no percentile is shown at all; below '
    + '1000 the answer is a band of ten and never a point. When it refuses it shows the real counts, '
    + 'because "not yet" with a number attached is actionable and "not yet" on its own is not.');

  if (!numeric.length) {
    return section('Percentile', head,
      empty('This run has no numeric output to place in a population. A range or a category is not '
        + 'ranked, and this app will not turn one into a number so that it can be.'));
  }

  const metricSelect = el('select', { class: 'input' },
    el('option', { value: '' }, 'choose a metric…'),
    ...numeric.map((o) => el('option', {
      value: o.metric_id,
      ...(view.metricId === o.metric_id ? { selected: true } : {}),
    }, o.label)));
  metricSelect.addEventListener('change', () => {
    view.metricId = metricSelect.value || null;
    rerender();
  });

  const minorAllowsPublic = publicScopeAllowed(st.athlete?.subjectIsMinor);
  // A minor's scope is not merely defaulted to private — the public option does not exist to pick.
  if (!minorAllowsPublic) view.scope = 'private';

  const scopeControl = minorAllowsPublic
    ? (() => {
        const sel = el('select', { class: 'input', 'data-scope': 'selector' },
          el('option', { value: 'private', ...(view.scope === 'private' ? { selected: true } : {}) },
            'private — the athlete, their guardian and their coach'),
          el('option', { value: 'public', ...(view.scope === 'public' ? { selected: true } : {}) },
            'public — an outward surface'));
        sel.addEventListener('change', () => {
          view.scope = sel.value === 'public' ? 'public' : 'private';
          rerender();
        });
        return sel;
      })()
    : el('p', { class: 'note st-minor-private-only' },
        st.athlete?.subjectIsMinor === true
          ? 'This athlete is a minor, so there is no public option to choose. Under-18s are never '
            + 'publicly ranked or leaderboarded on any surface, at any population size — not '
            + 'anonymised, not redacted, absent. A private percentile is context for the athlete, '
            + 'their guardian and their coach, and it never leaves an authenticated surface.'
          : 'This athlete\'s age is not established here, so only the private scope is offered. An '
            + 'unknown age is never resolved to "adult".');

  const body = el('div');
  const wrap = section('Percentile', head,
    el('label', { class: 'label' }, 'Metric'), metricSelect,
    el('label', { class: 'label' }, 'Scope'), scopeControl,
    body);

  if (!view.metricId) {
    body.append(empty('No metric chosen.'));
    return wrap;
  }

  const p = await fetchPercentile(result.session_id, view.metricId, view.scope);
  if (isRefusal(p)) {
    body.append(refusalView(p, { onRetry: rerender }));
    return wrap;
  }
  body.append(percentileView(p.value.percentile));
  return wrap;
}
